import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUpdateUser } from '@/services/UserService'
import { GetParentalControlsLanguagesForContentQuery } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { getWebClient } from '../ApolloClient'
import { getParentalControlsForContent } from './ParentalControlsService'

type UseParentalControlsParams = {
  contentId: string
}

export const useParentalControls = ({ contentId }: UseParentalControlsParams) => {
  const { mutate: updateUser } = useUpdateUser()
  const [data, setData] = useState<GetParentalControlsLanguagesForContentQuery | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        // Create client inside effect to avoid unstable dependency churn
        const client = getWebClient()
        const result = await getParentalControlsForContent({ client, contentId })
        setData(result)
        if (result === null && contentId) {
          // getParentalControlsForContent returns null on fetch failure (already logged internally)
          setError(new Error('Failed to load parental controls'))
        }
      } catch (err) {
        const fetchError = err instanceof Error ? err : new Error('Unexpected error loading parental controls')
        setError(fetchError)
        logger().error('useParentalControls fetch failed unexpectedly', { contentId }, err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [contentId])

  const setUserSwearingEnabledPreference = useCallback(
    async (swearingPreference: boolean) => {
      const currentValue = data?.user?.muteAllSwearingPreferenceEnabled

      if (currentValue !== swearingPreference) {
        await updateUser({
          variables: {
            input: {
              muteAllSwearingPreferenceEnabled: swearingPreference,
            },
          },
        })

        setData((prevData) => {
          if (!prevData) return prevData
          return {
            ...prevData,
            user: {
              ...(prevData.user ?? { __typename: 'User' as const }),
              muteAllSwearingPreferenceEnabled: swearingPreference,
            },
          }
        })
      }
      return null
    },
    [data?.user?.muteAllSwearingPreferenceEnabled, updateUser],
  )

  const skipLanguagesAvailable = useMemo(() => {
    if (data?.getContent && 'muteAllSwearsAvailability' in data.getContent) {
      return (
        data.getContent.muteAllSwearsAvailability?.map((availableLocale) => {
          return availableLocale ? availableLocale : null
        }) || []
      )
    }

    return []
  }, [data?.getContent])

  const hasSkipLanguagesAvailable = useMemo(() => {
    return skipLanguagesAvailable.length > 0
  }, [skipLanguagesAvailable])

  const userHasSelectedAudioPreference = useMemo(() => {
    // We need to check if the user has selected an audio preference to determine if the sheet should be open
    return data?.user?.muteAllSwearingPreferenceEnabled != null
  }, [data?.user?.muteAllSwearingPreferenceEnabled])

  const userEnabled = useMemo(() => {
    return data?.user?.muteAllSwearingPreferenceEnabled ?? false
  }, [data?.user?.muteAllSwearingPreferenceEnabled])

  return {
    loading,
    error,
    userEnabled,
    userHasSelectedAudioPreference,
    shouldShowParentalControls: !loading && hasSkipLanguagesAvailable && !userHasSelectedAudioPreference,
    skipLanguagesAvailable,
    hasSkipLanguagesAvailable,
    setUserSwearingEnabledPreference,
  }
}
